import { useQuery } from '@tanstack/react-query'
import {
  compressionSaved, createSourcesApi, createStreamsApi,
  fleetCompressionSaved, formatBytes, formatCompactCount, formatCount,
  formatPercent, workbookCount,
} from '@bloo-team/api-client'
import type { DdmStream, Stream, StreamCoverage, StreamsResult } from '@bloo-team/api-client'
import { client } from './api'
import { isDemoSession } from './demo'

/**
 * Streams data — GET /{clusterId}/mgmt/core/stream-management/.
 *
 * The endpoint ignores pagination and returns every stream in one response
 * (53 in the capture), so paging, filtering and counting all happen
 * client-side here. Unlike Sources, that means the counts are accurate over
 * the whole set rather than one page.
 *
 * Demo mode returns fixtures in the same shape, validated against the live
 * schema in the tests so it cannot drift.
 */

export const streamsApi = createStreamsApi(client)
const sourcesForStreams = createSourcesApi(client)

export {
  compressionSaved, fleetCompressionSaved, formatBytes, formatCompactCount,
  formatCount, formatPercent, workbookCount,
}
export type { DdmStream, Stream, StreamCoverage }

/**
 * Devices are fetched in ONE request: device-management honoured pagesize=1000
 * and returned all 807, so the sources-per-stream count needs no paging loop.
 */
const ALL_DEVICES_PAGE_SIZE = 1000

const wb = (total: number, scheduled: number, streamed: number) => ({
  total_workbook_count: total,
  inactive_total_workbook_count: 0,
  scheduled: { scheduled_workbook_count: scheduled, status: scheduled > 0, ids: [] },
  streamed: { streamed_workbook_count: streamed, status: streamed > 0, ids: [] },
})

const demo = (
  name: string, size: number, evt: number, total: number,
  workbooks: ReturnType<typeof wb>,
): Stream => ({
  stream: name,
  size_on_disk: size,
  evt_len: evt,
  total_len: total,
  retention: 90,
  replication: 1,
  compression: 'High',
  running_workbooks: workbooks,
})

/** Exported so tests can assert these validate against the live-captured schema. */
export const DEMO_STREAMS: Stream[] = [
  demo('APACHE-EVENT', 7337788737, 221668548, 828574282, wb(0, 0, 0)),
  demo('AUDITD', 41203847362, 88123400, 128740114, wb(28, 0, 28)),
  demo('AUTHENTICATION', 818192463449, 512900311, 728318441, wb(13, 6, 7)),
  demo('CLOUDTRAIL', 9182736450, 41200311, 61200114, wb(1, 0, 1)),
  demo('CONFIGURATION', 1458533, 12033, 17114, wb(2, 0, 2)),
  demo('EMAIL-GATEWAY', 3182736450, 22200311, 31200114, wb(8, 8, 0)),
  demo('FORTIGATE', 22182736450, 122200311, 181200114, wb(0, 0, 0)),
  demo('WIN-AUDIT', 63182736450, 322200311, 481200114, wb(0, 0, 0)),
]

/* --------------------------------------------------------------- data model */

/**
 * Demo data model — the `ddm/list` twin (docs/data-model-requirements.md
 * §Demo mode). ONE fixture for every reader: fabric Data Model, the
 * Extractors stages (`ddmFieldMap` gives them the old `Record<stream, fields>`)
 * and SynthAI Knowledge.
 *
 * Shape follows the 2026-08-26 capture (inventory/ddm-field-map.md): bare
 * field names, `stream_id` = `<STREAM>_<scope>`, epoch-second strings,
 * `created_by: 'DNIF'` on stock rows. All eight `DEMO_STREAMS` are present
 * (so "has data" lights up); the rest are declared-but-empty, as most of the
 * live tenant's 139 are. `GCPD-BADGE` is the one custom stream.
 *
 * AUTHENTICATION / FIREWALL / WEBFILTER / WIN-AUDIT / EP-PROCESS / OTHER keep
 * the exact field lists the extractor simulator tests were written against.
 */
const DDM_SCOPE = 'gotham'
const DDM_CREATED = '1741197529'

const ddm = (stream: string, fields: string[], updated: string, author = 'DNIF'): DdmStream => ({
  stream,
  stream_id: `${stream}_${DDM_SCOPE}`,
  fields,
  scope_id: DDM_SCOPE,
  created_by: author,
  updated_by: author,
  create_tstamp: author === 'DNIF' ? DDM_CREATED : updated,
  update_tstamp: updated,
})

export const DEMO_DDM: DdmStream[] = [
  // --- the eight DEMO_STREAMS (stream-management reports bytes for these)
  ddm('APACHE-EVENT', ['CNAMTime', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'LogType', 'Message', 'PStatus', 'SourceName', 'SourceType', 'Stream', 'System', 'SystemTstamp'], '1787833855'),
  ddm('AUDITD', ['Action', 'CNAMTime', 'CommandLine', 'DevSrcIP', 'Domain', 'DstHost', 'DstIP', 'EventName', 'EvtLen', 'Exe', 'ExtractorID', 'Hash', 'LogEvent', 'PStatus', 'Pid', 'Process', 'SourceName', 'SourceType', 'SrcHost', 'SrcIP', 'Stream', 'Syscall', 'System', 'SystemTstamp', 'TargetUser', 'User', 'UserID'], '1787833855'),
  ddm('AUTHENTICATION', ['Action', 'AuthProto', 'CNAMTime', 'DevSrcIP', 'EStatus', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'PStatus', 'Reason', 'SourceName', 'SourceType', 'SrcIP', 'Status', 'Stream', 'System', 'SystemTstamp', 'TargetUser', 'Type', 'User'], '1787920255'),
  ddm('CLOUDTRAIL', ['AccountId', 'CNAMTime', 'DevSrcIP', 'ErrorCode', 'EventName', 'EventSource', 'EvtLen', 'ExtractorID', 'LogEvent', 'PStatus', 'QueryName', 'Region', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'User', 'UserAgent'], '1787833855'),
  ddm('CONFIGURATION', ['Action', 'ActorIPAddress', 'CNAMTime', 'CommandLine', 'DevSrcIP', 'DeviceIP', 'Domain', 'DomainName', 'DstIP', 'EventName', 'EvtLen', 'ExtractorID', 'File', 'FileName', 'FilePath', 'Hash', 'LogEvent', 'Object', 'PStatus', 'Process', 'QueryName', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'TargetUser', 'URL', 'User', 'UserID'], '1787833855'),
  ddm('EMAIL-GATEWAY', ['Action', 'ActorIPAddress', 'CNAMTime', 'DevSrcIP', 'DeviceIP', 'Domain', 'DstIP', 'EventName', 'EvtLen', 'ExtractorID', 'File', 'FileName', 'LogEvent', 'PStatus', 'Recipient', 'Sender', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'Subject', 'System', 'SystemTstamp', 'User', 'UserID', 'Verdict'], '1787747455'),
  ddm('FORTIGATE', ['CNAMTime', 'DevSrcIP', 'Domain', 'DstIP', 'DstPort', 'EventName', 'EvtLen', 'ExtractorID', 'Level', 'LogEvent', 'PStatus', 'Policy', 'Proto', 'Query', 'Service', 'SourceName', 'SourceType', 'SrcIP', 'SrcPort', 'Stream', 'Subtype', 'System', 'SystemTstamp', 'TargetUser', 'Type', 'URL', 'User'], '1787833855'),
  ddm('WIN-AUDIT', ['CNAMTime', 'EID', 'ExtractorID', 'LogEvent', 'System', 'User'], '1787920255'),
  // --- the streams the demo extractor YAMLs write (no bytes in demo)
  ddm('FIREWALL', ['Action', 'CNAMTime', 'DstIP', 'DstPort', 'EvtLen', 'ExtractorID', 'LogEvent', 'PStatus', 'Proto', 'SourceName', 'SourceType', 'SrcIP', 'SrcPort', 'Status', 'Stream', 'System', 'SystemTstamp', 'TXLen', 'RXLen'], '1787833855'),
  ddm('WEBFILTER', ['Action', 'CNAMTime', 'DstIP', 'EvtLen', 'ExtractorID', 'LogEvent', 'PStatus', 'SourceName', 'SourceType', 'SrcIP', 'Status', 'Stream', 'System', 'SystemTstamp', 'URL', 'User'], '1787833855'),
  ddm('EP-PROCESS', ['CNAMTime', 'CommandLine', 'ExtractorID', 'Image', 'LogEvent', 'ParentImage', 'ProcessId', 'System', 'User'], '1787833855'),
  ddm('OTHER', ['CNAMTime', 'EventName', 'ExtractorID', 'LogEvent', 'SourceName', 'SourceType', 'Stream', 'System'], '1741197529'),
  // --- declared-but-empty stock streams (inventory/ddm-field-map.md)
  ddm('ARCOS-DBEVT', ['AuthProto', 'CNAMTime', 'DevSrcIP', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'User'], '1741197529'),
  ddm('ATLASSIAN-EVENT', ['Action', 'CNAMTime', 'DevSrcIP', 'Domain', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'SourceName', 'SourceType', 'SrcIP', 'Status', 'Stream', 'System', 'SystemTstamp', 'TargetUser', 'User'], '1741197529'),
  ddm('AUDITBEAT', ['CNAMTime', 'CommandLine', 'DevSrcIP', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'Process', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'User'], '1741197529'),
  ddm('AWS-VPC', ['Action', 'Bytes', 'CNAMTime', 'DstIP', 'DstPort', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'Packets', 'Proto', 'SourceName', 'SourceType', 'SrcIP', 'SrcPort', 'Stream', 'System', 'SystemTstamp'], '1741197529'),
  ddm('AZURE', ['Action', 'CNAMTime', 'DevSrcIP', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'SourceName', 'SourceType', 'SrcIP', 'Status', 'Stream', 'System', 'SystemTstamp', 'TargetUser', 'User'], '1741197529'),
  ddm('BOX-EVENT', ['CNAMTime', 'DevSrcIP', 'EventName', 'EvtLen', 'ExtractorID', 'File', 'LogEvent', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'User'], '1741197529'),
  ddm('CISCO-DUO', ['CNAMTime', 'DevSrcIP', 'EventName', 'EvtLen', 'ExtractorID', 'Factor', 'LogEvent', 'Reason', 'SourceName', 'SourceType', 'SrcIP', 'Status', 'Stream', 'System', 'SystemTstamp', 'User'], '1741197529'),
  // CROWDSTRIKE-FALCON carries ComputerName, not System — the capture's one such stream.
  ddm('CROWDSTRIKE-FALCON', ['CNAMTime', 'CommandLine', 'ComputerName', 'DevSrcIP', 'EventName', 'EvtLen', 'ExtractorID', 'File', 'FilePath', 'LogEvent', 'Severity', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'SystemTstamp', 'Tactic', 'Technique', 'User'], '1741197529'),
  ddm('CYBERARK', ['CNAMTime', 'CommandLine', 'DevSrcIP', 'DstIP', 'EventName', 'EvtLen', 'ExtractorID', 'File', 'LogEvent', 'Process', 'Safe', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'TargetUser', 'URL', 'User'], '1741197529'),
  ddm('OKTA', ['CNAMTime', 'DevSrcIP', 'Domain', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'Outcome', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'TargetUser', 'URL', 'User'], '1741197529'),
  ddm('PROXY', ['Action', 'CNAMTime', 'DevSrcIP', 'DstHost', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'URL', 'User'], '1741197529'),
  ddm('DNS', ['Action', 'CNAMTime', 'DevSrcIP', 'Domain', 'DstIP', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'Object', 'Query', 'QueryName', 'QueryType', 'SourceName', 'SourceType', 'SrcIP', 'Stream', 'System', 'SystemTstamp', 'User', 'UserID'], '1741197529'),
  // --- the one custom stream: a Content Manager declared it
  ddm('GCPD-BADGE', ['Action', 'BadgeId', 'CNAMTime', 'Door', 'EventName', 'EvtLen', 'ExtractorID', 'LogEvent', 'SourceName', 'SourceType', 'Stream', 'System', 'SystemTstamp', 'User'], '1756800000', 'renee'),
]

/** `ddm/list` rows → `Record<stream, fields>` — the shape the Extractors stages read (`useDdm`). */
export function ddmFieldMap(rows: DdmStream[]): Record<string, string[]> {
  return Object.fromEntries(rows.map((r) => [r.stream, r.fields]))
}

/** Sources feeding each stream, from the devices' `stream` arrays. */
export function streamSourceCounts(devices: Array<{ stream: string[] }>): Record<string, number> {
  const out: Record<string, number> = {}
  for (const d of devices) for (const name of d.stream) out[name] = (out[name] ?? 0) + 1
  return out
}

/** Demo coverage. Deliberately omits some streams, as the real endpoint does. */
export const DEMO_COVERAGE: Record<string, StreamCoverage> = {
  'AUTHENTICATION': { tactics: 7, techniques: 18, workbooks: 66, active_tactics: 2, active_techniques: 4, active_workbooks: 8 },
  'AUDITD': { tactics: 11, techniques: 38, workbooks: 96, active_tactics: 0, active_techniques: 0, active_workbooks: 0 },
  'CLOUDTRAIL': { tactics: 4, techniques: 9, workbooks: 21, active_tactics: 1, active_techniques: 2, active_workbooks: 3 },
  'WIN-AUDIT': { tactics: 9, techniques: 27, workbooks: 88, active_tactics: 3, active_techniques: 6, active_workbooks: 11 },
  'FORTIGATE': { tactics: 5, techniques: 12, workbooks: 30, active_tactics: 0, active_techniques: 0, active_workbooks: 0 },
  // APACHE-EVENT, CONFIGURATION and EMAIL-GATEWAY intentionally absent.
}

/** Event counts for today so far, in the same order of magnitude as live.
 * EMAIL-GATEWAY is deliberately DEAD (zero today): it exercises the coverage
 * health lens — an armed detection reading it shows as starved/degraded. */
export const DEMO_DAILY_EVENTS: Record<string, number> = {
  'APACHE-EVENT': 15489134, 'AUDITD': 88123400, 'AUTHENTICATION': 287585906,
  'CLOUDTRAIL': 41200311, 'CONFIGURATION': 12033, 'EMAIL-GATEWAY': 0,
  'FORTIGATE': 16044297, 'WIN-AUDIT': 134117095,
}

export const DEMO_SOURCE_COUNTS: Record<string, number> = {
  'APACHE-EVENT': 4, 'AUDITD': 12, 'AUTHENTICATION': 76, 'CLOUDTRAIL': 2,
  'CONFIGURATION': 0, 'EMAIL-GATEWAY': 3, 'FORTIGATE': 9, 'WIN-AUDIT': 39,
}

export interface UseStreamsArgs {
  clusterId: string
  scopeId: string
  searchType?: 'online' | 'historic'
}

export function useStreams({ clusterId, scopeId, searchType = 'online' }: UseStreamsArgs) {
  const isDemo = isDemoSession()
  return useQuery<StreamsResult>({
    queryKey: ['streams', clusterId, scopeId, searchType, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) return { streams: DEMO_STREAMS, total: DEMO_STREAMS.length }
      return streamsApi.list({ clusterId, scopeId, searchType })
    },
    retry: 1,
  })
}

/**
 * Coverage, ingest and source counts, each as its own query.
 *
 * Deliberately separate rather than one Promise.all: if analytics fails the
 * table still renders with the columns it does have, instead of the whole page
 * failing on a secondary metric.
 */
export function useStreamCoverage({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<Record<string, StreamCoverage>>({
    queryKey: ['stream-coverage', clusterId, scopeId, isDemo ? 'demo' : 'live'],
    queryFn: async () => (isDemo ? DEMO_COVERAGE : streamsApi.coverage({ clusterId, scopeId })),
    retry: 1,
  })
}

/** Events per stream so far today. A COUNT, not bytes — see the api-client docs. */
export function useStreamDailyEvents({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<Record<string, number>>({
    queryKey: ['stream-daily-events', clusterId, scopeId, isDemo ? 'demo' : 'live'],
    queryFn: async () => (isDemo ? DEMO_DAILY_EVENTS : streamsApi.dailyEventCount({ clusterId, scopeId })),
    retry: 1,
  })
}

export function useStreamSourceCounts({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<Record<string, number>>({
    queryKey: ['stream-sources', clusterId, scopeId, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) return DEMO_SOURCE_COUNTS
      const page = await sourcesForStreams.list({
        clusterId, scopeId, pageNo: 1, pageSize: ALL_DEVICES_PAGE_SIZE,
      })
      return streamSourceCounts(page.devices)
    },
    retry: 1,
  })
}

/* ------------------------------------------------------------------ display */

/** A stream plus everything the secondary endpoints know about it. */
export interface EnrichedStream {
  stream: Stream
  /** undefined = analytics has no entry for this stream (unknown, NOT zero). */
  coverage: StreamCoverage | undefined
  /** Events so far today. undefined = no figure returned. A COUNT, not bytes. */
  eventsToday: number | undefined
  sources: number
  /** null when a per-stream compression figure would not be meaningful. */
  saved: number | null
}

export function enrich(
  streams: Stream[],
  coverage: Record<string, StreamCoverage> | undefined,
  daily: Record<string, number> | undefined,
  sources: Record<string, number> | undefined,
): EnrichedStream[] {
  return streams.map((s) => ({
    stream: s,
    coverage: coverage?.[s.stream],
    eventsToday: daily?.[s.stream],
    sources: sources?.[s.stream] ?? 0,
    saved: compressionSaved(s),
  }))
}

export type StreamFilter = 'all' | 'with-workbooks' | 'idle'

/** A stream is "idle" when nothing is running against it. */
export function matchesFilter(s: Stream, filter: StreamFilter): boolean {
  if (filter === 'all') return true
  return filter === 'with-workbooks' ? workbookCount(s) > 0 : workbookCount(s) === 0
}

/** Largest on disk first — the reason to open this page is usually storage. */
export function bySizeDesc(a: Stream, b: Stream): number {
  return b.size_on_disk - a.size_on_disk || a.stream.localeCompare(b.stream)
}

export type SortKey =
  | 'stream' | 'size' | 'sources' | 'events'
  | 'techniques' | 'active' | 'workbooks' | 'saved' | 'retention'
export type SortDir = 'asc' | 'desc'

/**
 * Missing values always sort last, whichever direction is active — a stream
 * with unknown coverage should not outrank one with real numbers just because
 * the sort flipped.
 */
function cmp(a: number | null | undefined, b: number | null | undefined, dir: SortDir): number {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  return dir === 'asc' ? a - b : b - a
}

export function sortEnriched(rows: EnrichedStream[], key: SortKey, dir: SortDir): EnrichedStream[] {
  const value = (r: EnrichedStream): number | null | undefined => {
    switch (key) {
      case 'size': return r.stream.size_on_disk
      case 'sources': return r.sources
      case 'events': return r.eventsToday
      case 'techniques': return r.coverage?.techniques
      case 'active': return r.coverage?.active_techniques
      case 'workbooks': return workbookCount(r.stream)
      case 'saved': return r.saved
      case 'retention': return r.stream.retention
      default: return undefined
    }
  }
  return [...rows].sort((a, b) => {
    if (key === 'stream') {
      const n = a.stream.stream.localeCompare(b.stream.stream)
      return dir === 'asc' ? n : -n
    }
    // Name break makes the order stable rather than dependent on input order.
    return cmp(value(a), value(b), dir) || a.stream.stream.localeCompare(b.stream.stream)
  })
}

/** "6 streamed · 7 scheduled", omitting whichever is zero. Empty when none. */
export function workbookBreakdown(s: Stream): string {
  const streamed = s.running_workbooks.streamed?.streamed_workbook_count ?? 0
  const scheduled = s.running_workbooks.scheduled?.scheduled_workbook_count ?? 0
  const parts: string[] = []
  if (streamed) parts.push(`${streamed} streamed`)
  if (scheduled) parts.push(`${scheduled} scheduled`)
  return parts.join(' · ')
}
